// Shared dashboard chrome for the dark-theme pages (Home / Browse / Timetable rhythm).
// DesktopShell = top bar + full-height sidebar + scrollable content.
// MobileShell  = sticky top bar + padded content (bottom nav lives in DashboardLayout).
//
// Both mirror CampusMapDesktop / CampusMapMobile exactly so every page stays consistent.

import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Home, Calendar, LayoutGrid, MapPin, Bus,
  FileText, Bot, MessageSquare, ClipboardList, Search, Bell, PackageSearch,
} from 'lucide-react';
import Logo from '@/components/Logo';
import { DesktopNav } from '@/components/DesktopNav';
import { useDashboard } from '@/contexts/DashboardContext';
import { useUserPreferences } from '@/store/useStore';

// ─── Design tokens ────────────────────────────────────────────────────────────
export const T = {
  bg:         '#0A0A0A',
  surface:    'rgba(255,255,255,.03)',
  surfaceHov: 'rgba(255,255,255,.055)',
  border:     'rgba(255,255,255,.07)',
  borderHov:  'rgba(255,255,255,.13)',
  teal:       '#14B8A6',
  tealBg:     'rgba(20,184,166,.1)',
  tealBorder: 'rgba(20,184,166,.22)',
  text:       '#fff',
  text50:     'rgba(255,255,255,.5)',
  text35:     'rgba(255,255,255,.35)',
  text25:     'rgba(255,255,255,.25)',
  text15:     'rgba(255,255,255,.15)',
  mono:       "'JetBrains Mono', monospace",
  serif:      "'Instrument Serif', serif",
  sans:       "'Outfit', sans-serif",
};

// ─── Nav config ───────────────────────────────────────────────────────────────
const MAIN_NAV = [
  { path: '/dashboard/home',     Icon: Home,       label: 'Home'       },
  { path: '/dashboard/personal', Icon: Calendar,   label: 'Timetable'  },
  { path: '/dashboard/browse',   Icon: LayoutGrid, label: 'Browse'     },
  { path: '/dashboard/rooms',    Icon: MapPin,     label: 'Campus Map' },
  { path: '/dashboard/bus',      Icon: Bus,        label: 'Bus Routes' },
  { path: '/dashboard/lost-found', Icon: PackageSearch, label: 'Lost & Found' },
];
const ACADEMIC_NAV = [
  { path: '/dashboard/calendar',   Icon: Calendar,      label: 'Academics' },
  { path: '/dashboard/exams',      Icon: FileText,      label: 'Exams'     },
  { path: '/dashboard/owlet',      Icon: Bot,           label: 'Owlet AI'  },
  { path: '/dashboard/feedback',   Icon: MessageSquare, label: 'Feedback'  },
  { path: '/dashboard/attendance', Icon: ClipboardList, label: 'Attendance'},
];
const TOP_NAV = [
  { path: '/dashboard/home',     label: 'Home'      },
  { path: '/dashboard/personal', label: 'Timetable' },
  { path: '/dashboard/browse',   label: 'Browse'    },
  { path: '/dashboard/rooms',    label: 'Rooms'     },
  { path: '/dashboard/exams',    label: 'Exams'     },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
export function numberToRoman(num: number | string) {
  const n = typeof num === 'string' ? parseInt(num) : num;
  return (['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'][n]) || String(num);
}

// ─── Primitives ───────────────────────────────────────────────────────────────
export function MonoLabel({ children, color = T.text25, size = 9 }: { children: React.ReactNode; color?: string; size?: number }) {
  return (
    <span style={{
      fontFamily: T.mono, fontSize: size, letterSpacing: '.14em',
      color, textTransform: 'uppercase' as const,
    }}>{children}</span>
  );
}

export function SectionHeader({ title, meta }: { title: string; meta?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
      <span style={{ fontFamily: T.serif, fontSize: 20, fontWeight: 400, letterSpacing: '-.01em' }}>{title}</span>
      {meta && <MonoLabel size={8}>{meta}</MonoLabel>}
    </div>
  );
}

export function EmptyState({ icon: Icon, text }: { icon: any; text: string }) {
  return (
    <div style={{
      background: T.surface, border: `1px dashed ${T.border}`,
      borderRadius: 14, padding: '40px 24px', textAlign: 'center',
    }}>
      <Icon size={26} color={T.text15} style={{ margin: '0 auto 12px', display: 'block' }} aria-hidden />
      <p style={{ fontFamily: T.mono, fontSize: 10, letterSpacing: '.12em', color: T.text25, margin: 0 }}>{text}</p>
    </div>
  );
}

function SidebarLink({ path, Icon, label }: { path: string; Icon: any; label: string }) {
  const { pathname } = useLocation();
  const active = pathname === path || pathname.startsWith(path + '/');
  const [hov, setHov] = useState(false);
  return (
    <Link to={path} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 9, padding: '9px 10px',
        borderRadius: 9, textDecoration: 'none', fontSize: 12, transition: '.15s',
        background: active ? T.tealBg : hov ? 'rgba(255,255,255,.04)' : 'transparent',
        border: `1px solid ${active ? T.tealBorder : 'transparent'}`,
        color: active ? T.teal : hov ? 'rgba(255,255,255,.75)' : 'rgba(255,255,255,.4)',
      }}>
      <Icon size={15} aria-hidden /><span>{label}</span>
    </Link>
  );
}

function TopNavLink({ path, label }: { path: string; label: string }) {
  const { pathname } = useLocation();
  const active = pathname === path || pathname.startsWith(path + '/');
  const [hov, setHov] = useState(false);
  return (
    <Link to={path} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        fontSize: 12, textDecoration: 'none', transition: '.2s', paddingBottom: 2,
        color: active ? '#fff' : hov ? 'rgba(255,255,255,.65)' : 'rgba(255,255,255,.35)',
        borderBottom: active ? '1px solid rgba(20,184,166,.5)' : '1px solid transparent',
      }}>{label}</Link>
  );
}

function NavIconBtn({ children, onClick, badge, size = 32 }: { children: React.ReactNode; onClick?: () => void; badge?: number; size?: number }) {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        width: size, height: size, borderRadius: 10, flexShrink: 0,
        background: hov ? 'rgba(255,255,255,.09)' : 'rgba(255,255,255,.05)',
        border: `1px solid ${T.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: hov ? '#fff' : T.text50,
        cursor: 'pointer', transition: '.2s', position: 'relative',
      }}>
      {children}
      {badge && badge > 0 ? (
        <span style={{
          position: 'absolute', top: -3, right: -3, width: 14, height: 14,
          borderRadius: '50%', background: '#EF4444', border: `2px solid ${T.bg}`,
          fontSize: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, color: '#fff',
        }}>{badge}</span>
      ) : null}
    </button>
  );
}

function SectionLabel({ text }: { text: string }) {
  return (
    <div style={{
      fontFamily: T.mono, fontSize: 8, letterSpacing: '.18em',
      color: 'rgba(255,255,255,.2)', padding: '10px 10px 6px',
    }}>{text}</div>
  );
}

// Shared top-bar action cluster (search / bell / avatar) wired to DashboardContext.
function TopBarActions({ avatarSize = 32, iconSize = 14 }: { avatarSize?: number; iconSize?: number }) {
  const dashboard = useDashboard();
  const userPreferences = useUserPreferences();
  const unreadCount = dashboard
    ? dashboard.generateNotifications().filter((n: any) => !dashboard.readNotifications.has(n.id)).length
    : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <NavIconBtn size={avatarSize} onClick={() => dashboard?.setShowSearch(true)}><Search size={iconSize} /></NavIconBtn>
      <NavIconBtn size={avatarSize} badge={unreadCount} onClick={() => {
        if (dashboard) {
          dashboard.setShowNotifications(!dashboard.showNotifications);
          if (!dashboard.showNotifications) setTimeout(() => dashboard.markNotificationsAsRead(), 100);
        }
      }}><Bell size={iconSize} /></NavIconBtn>
      <button onClick={() => dashboard?.setShowUserMenu(!dashboard?.showUserMenu)}
        style={{
          width: avatarSize, height: avatarSize, borderRadius: 10, flexShrink: 0,
          background: T.tealBg, border: `1px solid ${T.tealBorder}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: T.teal, fontFamily: T.mono, fontSize: 10, fontWeight: 500, cursor: 'pointer',
        }}>{userPreferences?.name?.charAt(0).toUpperCase() ?? 'U'}</button>
    </div>
  );
}

interface ShellProps {
  title: string;
  subtitle?: string;
  badge?: React.ReactNode;      // small chip rendered next to the title
  actions?: React.ReactNode;    // page-specific buttons rendered on the title row (right)
  loading?: boolean;
  children: React.ReactNode;
}

// ─── Desktop shell ──────────────────────────────────────────────────────────—
export function DesktopShell({ title, subtitle, badge, actions, loading, children }: ShellProps) {
  const userPreferences = useUserPreferences();

  if (loading) {
    return (
      <div className="hidden lg:flex" style={{ background: T.bg, minHeight: '100vh', alignItems: 'stretch' }}>
        <div style={{ width: 220, borderRight: `1px solid ${T.border}`, padding: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[40, 32, 32, 32, 32, 32, 32].map((h, i) => <div key={i} style={{ height: h, background: T.surface, borderRadius: 8, animation: 'shellpulse 1.5s ease-in-out infinite', animationDelay: `${i * .08}s` }} />)}
        </div>
        <div style={{ flex: 1, padding: 28, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[56, 320, 200].map((h, i) => <div key={i} style={{ height: h, background: T.surface, borderRadius: 12, animation: 'shellpulse 1.5s ease-in-out infinite', animationDelay: `${i * .1}s` }} />)}
        </div>
        <style>{`@keyframes shellpulse{0%,100%{opacity:.3}50%{opacity:.7}}`}</style>
      </div>
    );
  }

  return (
    <div className="hidden lg:flex" style={{ background: T.bg, fontFamily: T.sans, color: T.text, minHeight: '100vh', flexDirection: 'column' }}>
      {/* Top bar */}
      <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 28px', borderBottom: `1px solid ${T.border}`,
        background: T.bg, position: 'sticky', top: 0, zIndex: 50, flexShrink: 0,
      }}>
        <Logo size="sm" clickable navigateTo="/dashboard/home" />
        <div style={{ display: 'flex', gap: 24 }}>
          {TOP_NAV.map(({ path, label }) => <TopNavLink key={path} path={path} label={label} />)}
        </div>
        <TopBarActions />
      </header>

      {/* Body */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar */}
        <aside style={{ width: 220, flexShrink: 0, borderRight: `1px solid ${T.border}`, padding: '20px 16px', display: 'flex', flexDirection: 'column', background: T.bg, overflowY: 'auto' }}>
          <SectionLabel text="MAIN" />
          {MAIN_NAV.map(item => <SidebarLink key={item.path} {...item} />)}
          <SectionLabel text="ACADEMICS" />
          {ACADEMIC_NAV.map(item => <SidebarLink key={item.path} {...item} />)}
          <div style={{ flex: 1 }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 10px', borderTop: `1px solid ${T.border}`, marginTop: 8 }}>
            <div style={{ width: 30, height: 30, borderRadius: 9, background: T.tealBg, border: `1px solid ${T.tealBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.teal, fontFamily: T.mono, fontSize: 10, fontWeight: 500, flexShrink: 0 }}>
              {userPreferences?.name?.charAt(0).toUpperCase() ?? 'U'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userPreferences?.name || 'Student'}</div>
              <div style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.06em', color: T.text25, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {[userPreferences?.program, userPreferences?.semester ? `SEM ${numberToRoman(userPreferences.semester)}` : null, userPreferences?.group ? `GRP ${userPreferences.group}` : null].filter(Boolean).join(' · ')}
              </div>
            </div>
          </div>
        </aside>

        {/* Main content */}
        <main style={{ flex: 1, overflowY: 'auto', padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 22 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 6 }}>
                <h1 style={{ fontFamily: T.serif, fontSize: 30, fontWeight: 400, letterSpacing: '-.02em', margin: 0, lineHeight: 1 }}>{title}</h1>
                {badge}
              </div>
              {subtitle && <MonoLabel>{subtitle}</MonoLabel>}
            </div>
            {actions && <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>{actions}</div>}
          </div>
          {children}
        </main>
      </div>
    </div>
  );
}

// ─── Mobile shell ─────────────────────────────────────────────────────────────
export function MobileShell({ title, subtitle, badge, actions, loading, children }: ShellProps) {
  if (loading) {
    return (
      <div className="lg:hidden" style={{ background: T.bg, minHeight: '100vh', padding: 20 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {[80, 60, 140, 140, 140].map((h, i) => <div key={i} style={{ height: h, background: T.surface, borderRadius: 12, animation: 'shellpulse 1.5s ease-in-out infinite', animationDelay: `${i * .1}s` }} />)}
        </div>
        <style>{`@keyframes shellpulse{0%,100%{opacity:.4}50%{opacity:.7}}`}</style>
      </div>
    );
  }

  return (
    <div className="lg:hidden" style={{ background: T.bg, fontFamily: T.sans, color: T.text, minHeight: '100vh' }}>
      {/* Top bar — pad top for the status bar / notch in standalone PWA */}
      <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: 'calc(16px + env(safe-area-inset-top, 0px)) 20px 16px',
        borderBottom: `1px solid ${T.border}`,
        background: T.bg, position: 'sticky', top: 0, zIndex: 40,
      }}>
        <Logo size="sm" clickable navigateTo="/dashboard/home" />
        <DesktopNav />
        <TopBarActions avatarSize={34} iconSize={15} />
      </header>

      {/* Bottom padding clears the fixed bottom nav incl. the home indicator */}
      <div style={{ padding: '20px 20px calc(104px + env(safe-area-inset-bottom, 0px))', display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Page title */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
              <h1 style={{ fontFamily: T.serif, fontSize: 26, fontWeight: 400, letterSpacing: '-.02em', margin: 0, lineHeight: 1 }}>{title}</h1>
              {badge}
            </div>
            {subtitle && <MonoLabel size={8}>{subtitle}</MonoLabel>}
          </div>
          {actions && <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>{actions}</div>}
        </div>
        {children}
      </div>
    </div>
  );
}
